import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../../api";
import { CheckCircle2, Circle, Rocket } from "lucide-react";

const PHASE_COLORS = { foundation: "#006CFF", upskill: "#16A34A", career: "#9933FF" };

export const JourneyStepper = () => {
  const [j, setJ] = useState(null);
  const { pathname } = useLocation();
  const base = pathname.startsWith("/working_pro") ? "/working_pro" : "/student";
  const fix = (link) => link.replace(/^\/student/, base);
  useEffect(() => {
    api.get("/me/career/journey").then(r => setJ(r.data)).catch(() => setJ(false));
  }, []);
  if (j === null || j === false) return null;
  return (
    <div className="card card-pad" data-testid="journey-stepper">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0 flex-1 basis-[240px]">
          <span className="eyebrow inline-flex items-center gap-1"><Rocket size={12} className="text-signal" /> Your career journey</span>
          {/* text sizes step up at breakpoints so at 125% zoom / 1024px
              screens the title still fits alongside the percentage on one
              row; on very narrow layouts it wraps below cleanly. */}
          <h3 className="font-display font-bold text-base sm:text-lg lg:text-xl mt-1 leading-tight break-words">
            Learn → Assess → Certify → Match → Place → Grow
          </h3>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-black text-2xl sm:text-3xl leading-none tabular-nums" data-testid="journey-pct">{j.progress_pct}%</div>
          <div className="text-xs text-muted mt-1">{j.done}/{j.total} steps done</div>
        </div>
      </div>
      <div className="h-2 bg-rule mt-3 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700"
             style={{ width: `${j.progress_pct}%`, background: "linear-gradient(90deg,#006CFF,#9933FF)" }} />
      </div>
      {/* Fix layout: on tablets show 2 columns (not 3 cramped ones), on
          desktops show 3. Each phase card uses a fixed header block whose
          badge is on its own row so long labels like "Skill Upgradation"
          never collide with the numeric count.
          `xl:` breakpoint (>=1280px) — at `lg:` (1024–1279px) the stepper
          lives inside a 1fr column of the outer grid which leaves barely
          140px per cell; 3 cols there breaks the labels mid-word. */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {j.phases.map(ph => {
          const color = PHASE_COLORS[ph.key] || "#006CFF";
          const phDone = ph.steps.filter(s => s.done).length;
          return (
            <div key={ph.key}
                 className="border border-rule rounded-xl p-4 min-w-0"
                 data-testid={`journey-phase-${ph.key}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="font-display font-bold text-sm leading-tight min-w-0 truncate"
                     style={{ color }}
                     title={ph.label}>
                  {ph.label}
                </div>
                <span className="text-[11px] font-mono text-muted shrink-0 tabular-nums px-1.5 py-0.5 rounded-full bg-paper">
                  {phDone}/{ph.steps.length}
                </span>
              </div>
              <div className="text-[11px] text-muted mb-3 leading-snug">{ph.sub}</div>
              <div className="space-y-1">
                {ph.steps.map(s => (
                  <Link key={s.key} to={fix(s.link)}
                        className={`flex items-start gap-2 text-sm rounded-lg px-2 py-1.5 -mx-2 transition hover:bg-paper ${s.done ? "text-ink" : "text-muted"}`}
                        data-testid={`journey-step-${ph.key}-${s.key}`}>
                    {s.done
                      ? <CheckCircle2 size={15} style={{ color }} className="shrink-0 mt-0.5" />
                      : <Circle size={15} className="shrink-0 opacity-40 mt-0.5" />}
                    <span className={`leading-snug ${s.done ? "font-medium" : ""}`}>{s.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
